import {
    ONVAgeBarChartElement,
    IAgeSeries,
    ILandesverbandOption,
} from '@/Components/AgeChart/ONVAgeBarChartElement';

/** Name des KO-Bindings, ueber das ein ViewModel an <onv-age-bar-chart> andockt. */
export const ONV_AGE_BAR_CHART_BINDING = 'onvAgeBarChart';

/**
 * Wert, den das Binding erwartet. Alle Felder duerfen Observables oder
 * Roh-Werte sein (werden via ko.unwrap entpackt).
 */
export interface IOnvAgeBarChartBinding {
    series:                  IAgeSeries[] | KnockoutObservableArray<IAgeSeries>;
    loading?:                boolean | KnockoutObservable<boolean>;
    error?:                  string | null | KnockoutObservable<string | null>;
    onHorizonsChange?:       (horizons: number[]) => void;
    /** Optionale Liste auswählbarer Landesverbände (für die LV-Auswahl im Diagramm). */
    landesverbandOptions?:   ILandesverbandOption[] | KnockoutObservableArray<ILandesverbandOption>;
    /** Callback bei Änderung der Landesverband-Auswahl. */
    onLandesverbaendeChange?: (landesverbandIds: string[]) => void;
}

/**
 * Registriert das KO-Binding <c>onvAgeBarChart</c> (idempotent).
 *
 * Es ist die Bruecke zwischen KO-Observables und dem framework-agnostischen
 * Web Component und wird von mehreren Views genutzt (Startseite sowie Statistik
 * je Landesverband). Daher liegt es neben der Web Component und nicht in einem
 * einzelnen ViewModel.
 *
 *   • series / loading / error → werden als JS-Property / Attribut gesetzt (update)
 *   • horizons-change-Event     → ruft onHorizonsChange im ViewModel auf (init)
 */
export function registerOnvAgeBarChartBinding(): void {
    if (ko.bindingHandlers[ONV_AGE_BAR_CHART_BINDING]) return;

    ko.bindingHandlers[ONV_AGE_BAR_CHART_BINDING] = {

        init(element: ONVAgeBarChartElement, valueAccessor) {
            element.addEventListener(
                ONVAgeBarChartElement.EVENT_HORIZONS_CHANGE,
                (evt) => {
                    // valueAccessor() ergibt stets den aktuellen Binding-Wert.
                    const { onHorizonsChange } = ko.unwrap(valueAccessor());
                    const { horizons } = (evt as CustomEvent<{ horizons: number[] }>).detail;
                    onHorizonsChange?.(horizons);
                },
            );

            element.addEventListener(
                ONVAgeBarChartElement.EVENT_LV_CHANGE,
                (evt) => {
                    const { onLandesverbaendeChange } = ko.unwrap(valueAccessor());
                    const { landesverbandIds } = (evt as CustomEvent<{ landesverbandIds: string[] }>).detail;
                    onLandesverbaendeChange?.(landesverbandIds);
                },
            );
        },

        update(element: ONVAgeBarChartElement, valueAccessor) {
            const v       = ko.unwrap(valueAccessor());
            const series  = (ko.unwrap(v.series)  ?? []) as IAgeSeries[];
            const loading = ko.unwrap(v.loading)  as boolean;
            const error   = ko.unwrap(v.error)    as string | null;
            const lvOpts  = (ko.unwrap(v.landesverbandOptions) ?? []) as ILandesverbandOption[];

            // Landesverband-Optionen setzen (vor data, damit die Auswahl initial steht).
            element.landesverbandOptions = lvOpts;

            // Rohdaten als JS-Property setzen → löst Diagramm-Update aus.
            element.data = series.length > 0 ? { series } : null;

            // Lade- und Fehlerzustand als Attribute spiegeln.
            if (loading) element.setAttribute(ONVAgeBarChartElement.ATTR_LOADING, '');
            else         element.removeAttribute(ONVAgeBarChartElement.ATTR_LOADING);

            if (error) element.setAttribute(ONVAgeBarChartElement.ATTR_ERROR, error);
            else       element.removeAttribute(ONVAgeBarChartElement.ATTR_ERROR);
        },
    };
}

// Beim Import sofort registrieren – so genuegt ein einfacher Import in jeder View.
registerOnvAgeBarChartBinding();
